/**
 * Job Watcher Admin Hub.
 *
 * Standalone observability + control panel for the consumer "Watch" feature:
 *   - Live counts (companies / jobs / feed / radars)
 *   - On-demand run triggers (ingest BA/ATS/all, enrich+index, liveness, cleanup) — non-blocking
 *   - Live run status + summaries
 *   - Recently discovered companies / jobs
 *
 * Routed at /admin/job-watcher behind <AdminGuard>. Kept separate from the
 * consumer WatchView so the two UIs never bleed into each other.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Database, Play, RefreshCw, Loader2, X as XIcon, CheckCheck,
  Sparkles, Trash2, Clock, Search, PlusCircle, Globe,
} from 'lucide-react';

import {
  useWatchApi, AdminStats, MarqueeEntry, MarqueeRegistry,
  CompanySearchHit, ManualJobCreate, DiscoverResult,
  AdminCompanyDetail,
} from '../../services/watchApi';

const fmtEpoch = (ts: number | null | undefined): string => {
  if (!ts) return '—';
  const ms = ts < 1e12 ? ts * 1000 : ts;
  return new Date(ms).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' });
};

const fmtSql = (v: unknown): string => {
  if (v === null || v === undefined) return '—';
  if (typeof v === 'object') return JSON.stringify(v);
  if (typeof v === 'boolean') return v ? 'true' : 'false';
  return String(v);
};

const COMPANY_SQL_KEYS = [
  'id', 'name', 'slug', 'domain', 'normalized_name', 'source', 'ats_type', 'ats_slug',
  'discovered_at', 'is_active', 'created_by', 'industry', 'size', 'location', 'website',
  'logo_url', 'founded_year', 'funding_stage', 'created_at', 'updated_at',
  'bio', 'looking_for', 'cevee_tags', 'culture_tags',
] as const;

const CompanyDetailModal: React.FC<{
  data: AdminCompanyDetail | null;
  loading: boolean;
  error: string | null;
  onClose: () => void;
  onPickForManual?: (companyId: string, name: string, domain: string) => void;
}> = ({ data, loading, error, onClose, onPickForManual }) => {
  const [expandedJobId, setExpandedJobId] = useState<string | null>(null);
  const [showInactive, setShowInactive] = useState(true);

  const visibleJobs = useMemo(
    () => (data?.jobs || []).filter(j => showInactive || j.is_active),
    [data?.jobs, showInactive],
  );

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/40" onClick={onClose}>
      <div
        className="bg-white w-full sm:max-w-4xl max-h-[92vh] rounded-t-2xl sm:rounded-2xl shadow-xl flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 px-4 py-3 border-b border-slate-100">
          <div>
            <h2 className="text-lg font-bold text-slate-900">
              {data?.company?.name as string || 'Firma'}
            </h2>
            <p className="text-xs text-slate-500">
              SQL-Inspector · companies + jobs
            </p>
          </div>
          <button type="button" onClick={onClose} className="p-1 rounded-lg hover:bg-slate-100">
            <XIcon className="w-5 h-5 text-slate-500" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-4 py-3">
          {loading && (
            <div className="flex items-center gap-2 text-sm text-slate-500 py-8 justify-center">
              <Loader2 className="w-4 h-4 animate-spin" /> Lädt…
            </div>
          )}
          {error && !loading && (
            <p className="text-sm text-rose-600 py-4">{error}</p>
          )}
          {data && !loading && (
            <>
              {data.job_counts && (
                <p className="text-xs text-slate-600 mb-3">
                  {data.job_counts.jobs_active} aktiv · {data.job_counts.jobs_inactive} inaktiv ·{' '}
                  {data.job_counts.jobs_ba} BA · {data.job_counts.jobs_ats} ATS ·{' '}
                  {data.job_counts.jobs_manual} manual · {data.job_counts.jobs_indexed} indexiert
                </p>
              )}
              <p className="text-[10px] font-bold uppercase tracking-wide text-slate-400 mb-2">
                companies (SQL)
              </p>
              <div className="rounded-xl border border-slate-200 overflow-hidden mb-4 text-xs">
                <table className="w-full">
                  <tbody>
                    {COMPANY_SQL_KEYS.map(key => {
                      const val = data.company[key];
                      if (val === undefined) return null;
                      return (
                        <tr key={key} className="border-b border-slate-50 last:border-0">
                          <td className="py-1.5 px-2 font-mono text-slate-400 align-top w-36 shrink-0">{key}</td>
                          <td className="py-1.5 px-2 text-slate-800 break-all">{fmtSql(val)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="flex items-center justify-between gap-2 mb-2">
                <p className="text-[10px] font-bold uppercase tracking-wide text-slate-400">
                  jobs (SQL) · {visibleJobs.length} Zeilen
                  {data.jobs_truncated && ' · gekappt'}
                </p>
                <label className="flex items-center gap-1.5 text-[11px] text-slate-600">
                  <input
                    type="checkbox"
                    checked={showInactive}
                    onChange={e => setShowInactive(e.target.checked)}
                    className="rounded border-slate-300"
                  />
                  Inaktive zeigen
                </label>
              </div>
              <div className="rounded-xl border border-slate-200 overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-left text-[10px] uppercase text-slate-400 border-b border-slate-100">
                      <th className="py-2 px-2">Titel</th>
                      <th className="py-2 px-2">Src</th>
                      <th className="py-2 px-2">Aktiv</th>
                      <th className="py-2 px-2">Ort</th>
                      <th className="py-2 px-2">last_seen</th>
                      <th className="py-2 px-2">Idx</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleJobs.map(j => {
                      const b = sourceBadge(j.source);
                      const open = expandedJobId === j.id;
                      return (
                        <React.Fragment key={j.id}>
                          <tr
                            className="border-b border-slate-50 cursor-pointer hover:bg-pink-50/50"
                            onClick={() => setExpandedJobId(open ? null : j.id)}
                          >
                            <td className="py-2 px-2 font-medium text-slate-800 max-w-[200px] truncate">{j.title}</td>
                            <td className="py-2 px-2">
                              <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${b.cls}`}>{b.label}</span>
                            </td>
                            <td className="py-2 px-2">{j.is_active ? '✓' : '—'}</td>
                            <td className="py-2 px-2 text-slate-500 truncate max-w-[120px]">{j.location || '—'}</td>
                            <td className="py-2 px-2 text-slate-500 whitespace-nowrap">{fmtEpoch(j.last_seen_at)}</td>
                            <td className="py-2 px-2">{j.vector_indexed_at ? '✓' : '—'}</td>
                          </tr>
                          {open && (
                            <tr className="bg-slate-50">
                              <td colSpan={6} className="px-2 py-2">
                                <pre className="text-[10px] font-mono text-slate-700 whitespace-pre-wrap break-all max-h-48 overflow-y-auto">
                                  {JSON.stringify(j, null, 2)}
                                </pre>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
        {data && onPickForManual && (
          <div className="px-4 py-3 border-t border-slate-100 flex gap-2">
            <button
              type="button"
              onClick={() => onPickForManual(
                String(data.company.id),
                String(data.company.name || ''),
                String(data.company.domain || ''),
              )}
              className="rounded-lg bg-violet-600 px-3 py-2 text-sm font-semibold text-white hover:bg-violet-700"
            >
              Für Manual Job übernehmen
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const runSummary = (result: any): string => {
  if (!result || typeof result !== 'object') return '';
  if (result.bundesagentur || result.ats || result.list || result.big || result.curated || result.detect_seed) {
    const parts: string[] = [];
    if (result.detect_seed) {
      const r = result.detect_seed;
      parts.push(
        `Detect Seed: ${r.seen ?? 0} gesehen, ${r.detected ?? 0} ATS, ${r.no_ats ?? 0} ohne ATS, `
        + `${r.jobs_new ?? 0} neue Jobs`,
      );
    }
    if (result.detect_explore) {
      const r = result.detect_explore;
      parts.push(
        `Detect Explore: ${r.seen ?? 0} gesehen, ${r.detected ?? 0} ATS, `
        + `${r.domain_backfilled ?? 0} Domains, ${r.jobs_new ?? 0} neue Jobs`,
      );
    }
    if (result.curated) {
      const c = result.curated;
      if (c.list) {
        const r = c.list;
        parts.push(`Liste: ${r.imported ?? 0} importiert`);
      }
      if (c.big) {
        const r = c.big;
        parts.push(`Big: ${r.imported ?? 0} importiert`);
      }
      if (c.ats) {
        const r = c.ats;
        parts.push(`ATS: ${r.companies ?? 0} Firmen, ${r.new ?? 0} neu, ${r.updated ?? 0} akt., ${r.errors ?? 0} Fehler`);
      }
    }
    if (result.bundesagentur) {
      const r = result.bundesagentur;
      parts.push(`BA: ${r.fetched ?? 0} gefetcht, ${r.new ?? 0} neu, ${r.updated ?? 0} akt., ${r.errors ?? 0} Fehler`);
    }
    if (result.list) {
      const r = result.list;
      parts.push(`Liste: ${r.seen ?? 0} gesehen, ${r.imported ?? 0} importiert, ${r.errors ?? 0} Fehler`);
    }
    if (result.big) {
      const r = result.big;
      parts.push(`Big: ${r.seen ?? 0} gesehen, ${r.imported ?? 0} importiert, ${r.errors ?? 0} Fehler`);
    }
    if (result.ats) {
      const r = result.ats;
      parts.push(`ATS: ${r.companies ?? 0} Firmen, ${r.new ?? 0} neu, ${r.updated ?? 0} akt., ${r.no_ats ?? 0} ohne ATS, ${r.errors ?? 0} Fehler`);
    }
    return parts.join(' · ');
  }
  if (result.ingest && result.embed) {
    const i = result.ingest; const e = result.embed;
    return `Backfill: ${i.new ?? 0} neue Jobs · Embed: ${e.embedded ?? 0} indexiert`;
  }
  if ('embedded' in result || 'claimed' in result) {
    return `${result.claimed ?? 0} geclaimt, ${result.embedded ?? 0} indexiert, ${result.salary_found ?? 0} Gehalt, ${result.errors ?? 0} Fehler`;
  }
  if ('deleted_jobs' in result) {
    const mode = result.by_last_seen ? 'last_seen' : 'first_seen';
    return `Retention (${result.window_days}T, ${mode}): ${result.deleted_jobs} gelöscht, ${result.kept_interested} interessiert behalten, ${result.vectors_deleted} Vektoren weg`;
  }
  if (result.db) {
    const d = result.db;
    return `Reset: ${d.jobs_deleted} Jobs, ${d.feed_deleted} Feed, Radare: ${d.watchers} · Pinecone: ${result.pinecone || result.pinecone_error || '—'}`;
  }
  if ('jobs_deactivated' in result) {
    const parts = [
      `deaktiviert: ${result.jobs_deactivated}`,
      `Feed gelöscht: ${result.feed_deleted}`,
      `stale: ${result.stale_pruned}`,
    ];
    if (result.jobs_purged) parts.push(`purged: ${result.jobs_purged}`);
    if (result.vectors_purged) parts.push(`Vektoren: ${result.vectors_purged}`);
    if (result.stale_days) parts.unshift(`${result.stale_days}T stale`);
    return parts.join(', ');
  }
  return JSON.stringify(result);
};

const STAT_CELLS: { label: string; key: keyof AdminStats['counts'] }[] = [
  { label: 'Firmen (discovered)', key: 'companies_discovered' },
  { label: 'Jobs aktiv', key: 'jobs_active' },
  { label: 'Jobs inaktiv', key: 'jobs_inactive' },
  { label: 'Stale (aktiv)', key: 'jobs_stale_active' },
  { label: 'Jobs enriched', key: 'jobs_enriched' },
  { label: 'Jobs indexed', key: 'jobs_indexed' },
  { label: 'Feed neu', key: 'feed_new' },
  { label: 'Feed queued', key: 'feed_queued' },
  { label: 'Feed interessiert', key: 'feed_interested' },
  { label: 'Radare (role)', key: 'watchers_role' },
  { label: 'Auto an', key: 'auto_active' },
];

// Source → short label + color, so admins see where a job came from at a glance.
const SOURCE_BADGE: Record<string, { label: string; cls: string }> = {
  bundesagentur: { label: 'BA', cls: 'bg-rose-100 text-rose-700' },
  greenhouse: { label: 'GH', cls: 'bg-green-100 text-green-700' },
  lever: { label: 'Lever', cls: 'bg-violet-100 text-violet-700' },
  ashby: { label: 'Ashby', cls: 'bg-blue-100 text-blue-700' },
  smartrecruiters: { label: 'SR', cls: 'bg-cyan-100 text-cyan-700' },
  personio: { label: 'Personio', cls: 'bg-teal-100 text-teal-700' },
  workday: { label: 'Workday', cls: 'bg-amber-100 text-amber-700' },
  amazon: { label: 'Amazon', cls: 'bg-orange-100 text-orange-700' },
  manual: { label: 'Manual', cls: 'bg-violet-100 text-violet-700' },
};
const sourceBadge = (s: string) =>
  SOURCE_BADGE[s] || { label: s, cls: 'bg-slate-100 text-slate-600' };

const MARQUEE_STATUS: Record<MarqueeEntry['status'], { label: string; cls: string }> = {
  live: { label: 'Live (ATS)', cls: 'bg-emerald-100 text-emerald-800' },
  live_via_ba: { label: 'Via BA', cls: 'bg-sky-100 text-sky-800' },
  partial: { label: 'Teilweise', cls: 'bg-amber-100 text-amber-800' },
  empty: { label: '0 Jobs', cls: 'bg-slate-100 text-slate-600' },
  not_imported: { label: 'Nicht importiert', cls: 'bg-slate-100 text-slate-500' },
  not_found: { label: 'Nicht gefunden', cls: 'bg-rose-100 text-rose-700' },
};

const CATEGORY_LABELS: Record<string, string> = {
  all: 'Alle',
  gaps: 'Lücken',
  tracked: 'Tracked',
  blocked: 'Blocked',
  big_tech: 'Big Tech',
  mbb: 'MBB',
  dax: 'DAX',
  bank: 'Banken',
};

// Production ingest bounds (API defaults are tiny for safe dry-runs).
const INGEST_FULL = {
  dry_run: false as const,
  max_pages: 3,
  since_days: 14,
  limit: 500,
};

const emptyManualForm = (): ManualJobCreate & { company_name: string; domain: string } => ({
  company_name: '',
  domain: '',
  title: '',
  location: '',
  apply_url: '',
  description: '',
});

const JobWatcherHub: React.FC = () => {
  const api = useWatchApi();
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [marquee, setMarquee] = useState<MarqueeRegistry | null>(null);
  const [marqueeFilter, setMarqueeFilter] = useState('all');
  const [companyQuery, setCompanyQuery] = useState('');
  const [searchResults, setSearchResults] = useState<CompanySearchHit[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [manualForm, setManualForm] = useState(emptyManualForm());
  const [manualCompanyId, setManualCompanyId] = useState<string | null>(null);
  const [manualBusy, setManualBusy] = useState(false);
  const [discoverResult, setDiscoverResult] = useState<DiscoverResult | null>(null);
  const [discoverBusy, setDiscoverBusy] = useState(false);
  const [embedAfter, setEmbedAfter] = useState(true);
  const [companyDetail, setCompanyDetail] = useState<AdminCompanyDetail | null>(null);
  const [companyDetailLoading, setCompanyDetailLoading] = useState(false);
  const [companyDetailError, setCompanyDetailError] = useState<string | null>(null);
  const searchTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  const openCompanyDetail = useCallback(async (opts: { companyId?: string; domain?: string }) => {
    setCompanyDetailLoading(true);
    setCompanyDetailError(null);
    setCompanyDetail(null);
    try {
      setCompanyDetail(await api.adminCompanyDetail({
        companyId: opts.companyId,
        domain: opts.domain,
        jobLimit: 100,
        includeInactive: true,
      }));
    } catch (e: any) {
      setCompanyDetailError(e?.message || 'Firma konnte nicht geladen werden.');
    } finally {
      setCompanyDetailLoading(false);
    }
  }, [api]);

  const closeCompanyDetail = () => {
    setCompanyDetail(null);
    setCompanyDetailError(null);
  };

  const refresh = useCallback(async () => {
    try {
      setStats(await api.adminStats());
      setMarquee(await api.adminMarqueeRegistry());
    } catch { /* ignore */ }
  }, [api]);

  useEffect(() => { refresh(); }, [refresh]);

  // Live-poll while any background pass is running.
  const anyRunning = !!stats?.last_runs &&
    Object.values(stats.last_runs).some(r => r?.status === 'running');
  useEffect(() => {
    if (!anyRunning) return;
    const id = setInterval(refresh, 4000);
    return () => clearInterval(id);
  }, [anyRunning, refresh]);

  useEffect(() => {
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    const q = companyQuery.trim();
    if (q.length < 2) {
      setSearchResults([]);
      return;
    }
    searchTimeout.current = setTimeout(async () => {
      setSearchLoading(true);
      try {
        const r = await api.adminSearchCompanies(q);
        setSearchResults(r.results || []);
      } catch {
        setSearchResults([]);
      } finally {
        setSearchLoading(false);
      }
    }, 300);
    return () => { if (searchTimeout.current) clearTimeout(searchTimeout.current); };
  }, [companyQuery, api]);

  const pickCompany = (hit: CompanySearchHit) => {
    setManualCompanyId(hit.id);
    setManualForm(f => ({
      ...f,
      company_name: hit.name,
      domain: hit.domain || '',
    }));
    setCompanyQuery(hit.name);
    setSearchResults([]);
  };

  const pickMarquee = (e: MarqueeEntry) => {
    setManualCompanyId(e.company_id);
    setManualForm(f => ({
      ...f,
      company_name: e.name,
      domain: e.domain,
    }));
    setCompanyQuery(e.name);
    setDiscoverResult(null);
  };

  const runDiscover = async (ingest: boolean) => {
    const q = companyQuery.trim();
    if (q.length < 2) {
      setNote('Mindestens 2 Zeichen (Name, Domain oder Karriere-URL).');
      return;
    }
    setDiscoverBusy(true);
    setNote(null);
    if (!ingest) setDiscoverResult(null);
    try {
      const r = await api.adminDiscoverCompany({
        q,
        ingest,
        company_name: manualForm.company_name.trim() || undefined,
        embed_after: ingest && embedAfter,
      });
      setDiscoverResult(r);
      if (ingest && r.ingest) {
        setNote(
          `Import: ${r.ingest.new} neu, ${r.ingest.updated} aktualisiert`
          + (r.ingest.errors ? `, ${r.ingest.errors} Fehler` : '')
          + (r.embed_started ? ' · Enrich+Index läuft…' : ''),
        );
        if (r.ingest.company_id) setManualCompanyId(r.ingest.company_id);
        refresh();
      }
    } catch (e: any) {
      setNote(e?.message || 'Externe Suche fehlgeschlagen.');
    } finally {
      setDiscoverBusy(false);
    }
  };

  const submitManualJob = async () => {
    if (!manualForm.title.trim()) {
      setNote('Titel fehlt.');
      return;
    }
    if (!manualCompanyId && !manualForm.company_name.trim()) {
      setNote('Firma fehlt.');
      return;
    }
    setManualBusy(true);
    setNote(null);
    try {
      const body: ManualJobCreate = {
        title: manualForm.title.trim(),
        location: manualForm.location?.trim() || undefined,
        apply_url: manualForm.apply_url?.trim() || undefined,
        description: manualForm.description?.trim() || undefined,
        embed_after: embedAfter,
      };
      if (manualCompanyId) body.company_id = manualCompanyId;
      else {
        body.company_name = manualForm.company_name.trim();
        if (manualForm.domain.trim()) body.domain = manualForm.domain.trim();
      }
      const r = await api.adminCreateManualJob(body);
      setNote(
        r.inserted
          ? `Job angelegt.${r.embed_started ? ' Enrich+Index läuft…' : ''}`
          : `Job bereits vorhanden (Duplikat-Fingerprint).`,
      );
      setManualForm(emptyManualForm());
      setManualCompanyId(null);
      setCompanyQuery('');
      refresh();
    } catch (e: any) {
      setNote(e?.message || 'Job konnte nicht angelegt werden.');
    } finally {
      setManualBusy(false);
    }
  };

  const filteredMarquee = (marquee?.entries || []).filter(e => {
    if (marqueeFilter === 'all') return true;
    if (marqueeFilter === 'gaps') {
      return e.status === 'not_found' || e.status === 'empty' || e.status === 'not_imported';
    }
    if (marqueeFilter === 'tracked') return e.tier === 'tracked';
    if (marqueeFilter === 'blocked') return e.tier === 'blocked';
    return e.category === marqueeFilter;
  });

  const trigger = async (label: string, fn: () => Promise<any>) => {
    setBusy(label);
    setNote(null);
    try {
      await fn();
      setNote(`${label} läuft im Hintergrund. Status und Zahlen aktualisieren sich automatisch.`);
      setTimeout(refresh, 800);
      setTimeout(refresh, 3000);
    } catch (e: any) {
      setNote(e?.message || `${label} fehlgeschlagen.`);
    } finally {
      setBusy(null);
    }
  };

  const c = stats?.counts;

  const statCells = STAT_CELLS;

  const Btn: React.FC<{
    label: string; color: string; icon?: React.ReactNode; onClick: () => void;
  }> = ({ label, color, icon, onClick }) => (
    <button
      onClick={onClick}
      disabled={!!busy}
      className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-semibold text-white disabled:opacity-50 ${color}`}
    >
      {busy === label ? <Loader2 className="w-4 h-4 animate-spin" /> : (icon || <Play className="w-4 h-4" />)}
      {label}
    </button>
  );

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-5xl mx-auto px-4 py-6">
        <header className="mb-5">
          <h1 className="inline-flex items-center gap-2 text-2xl font-bold text-slate-900">
            <Database className="w-6 h-6 text-pink-500" /> Job Watcher · Admin
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Ingest (Bundesagentur + ATS), Matching und Retention manuell steuern · alles läuft non-blocking im Hintergrund.
            {stats?.pull_model && (
              <span className="text-emerald-600 font-medium"> · Pull-Modell aktiv (Index + Live-Radar)</span>
            )}
          </p>
        </header>

        {/* Coverage + sources (cheap SQL — no extra crawls) */}
        {stats?.coverage && (
          <div className="rounded-2xl bg-white border border-slate-200 p-4 shadow-sm mb-5">
            <div className="flex flex-wrap items-end justify-between gap-3 mb-3">
              <div>
                <p className="text-xs font-bold uppercase tracking-wide text-slate-400">
                  Marquee-Coverage
                </p>
                <p className="text-3xl font-bold text-slate-900">
                  {stats.coverage.pct}%
                  <span className="text-base font-normal text-slate-500 ml-2">
                    {stats.coverage.covered}/{stats.coverage.target_total} Ziel-Firmen
                  </span>
                </p>
                <p className="text-xs text-slate-500 mt-1">
                  Live / Via BA / Teilweise · {stats.coverage.gaps} Lücken · Registry{' '}
                  {stats.coverage.registry_tracked} tracked + {stats.coverage.registry_blocked} blocked
                </p>
              </div>
              <div className="text-right text-xs text-slate-500">
                Retention: {stats.coverage.stale_days}T deaktivieren ·{' '}
                {stats.coverage.retention_days}T purge (last_seen)
              </div>
            </div>
            {stats.jobs_by_source && Object.keys(stats.jobs_by_source).length > 0 && (
              <div className="flex flex-wrap gap-2 mb-3">
                {Object.entries(stats.jobs_by_source).map(([src, n]) => {
                  const b = sourceBadge(src);
                  return (
                    <span key={src} className={`rounded-full px-2.5 py-1 text-xs font-semibold ${b.cls}`}>
                      {b.label}: {n}
                    </span>
                  );
                })}
              </div>
            )}
            {stats.source_mix && (stats.source_mix.companies_with_jobs ?? 0) > 0 && (
              <div className="rounded-lg border border-slate-200 bg-slate-50/60 px-3 py-2 mb-3 text-xs">
                <p className="font-semibold text-slate-700 mb-1">
                  Firmen-Quellenmix ({stats.source_mix.companies_with_jobs} mit aktiven Jobs)
                </p>
                <div className="flex flex-wrap gap-2">
                  <span className="rounded-full bg-indigo-100 text-indigo-700 px-2.5 py-1 font-semibold">
                    BA + ATS: {stats.source_mix.both_ba_ats ?? 0}
                  </span>
                  <span className="rounded-full bg-emerald-100 text-emerald-700 px-2.5 py-1 font-semibold">
                    nur ATS: {stats.source_mix.ats_only ?? 0}
                  </span>
                  <span className="rounded-full bg-rose-100 text-rose-700 px-2.5 py-1 font-semibold">
                    nur BA: {stats.source_mix.ba_only ?? 0}
                  </span>
                  <span className="rounded-full bg-slate-200 text-slate-600 px-2.5 py-1 font-semibold">
                    mit Manual: {stats.source_mix.with_manual ?? 0}
                  </span>
                </div>
                {(stats.source_mix.both_samples?.length ?? 0) > 0 && (
                  <details className="mt-2">
                    <summary className="cursor-pointer text-slate-500 hover:text-slate-700">
                      Firmen mit BA + ATS zeigen ({stats.source_mix.both_samples!.length})
                    </summary>
                    <div className="mt-1.5 flex flex-wrap gap-1.5">
                      {stats.source_mix.both_samples!.map((s) => (
                        <span
                          key={`${s.name}-${s.domain ?? ''}`}
                          className="rounded-md border border-slate-200 bg-white px-2 py-0.5 text-slate-600"
                          title={s.domain ?? undefined}
                        >
                          {s.name}{' '}
                          <span className="text-rose-600 font-medium">{s.jobs_ba} BA</span>
                          {' · '}
                          <span className="text-emerald-600 font-medium">{s.jobs_ats} ATS</span>
                        </span>
                      ))}
                    </div>
                  </details>
                )}
              </div>
            )}
            {stats.coverage.by_category && Object.keys(stats.coverage.by_category).length > 0 && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
                {Object.entries(stats.coverage.by_category).map(([cat, row]) => (
                  <div key={cat} className="rounded-lg bg-slate-50 px-3 py-2 text-xs">
                    <p className="font-semibold text-slate-700">
                      {CATEGORY_LABELS[cat] || cat}
                    </p>
                    <p className="text-slate-500">
                      {row.covered}/{row.total} covered · {row.gaps} Lücken
                    </p>
                  </div>
                ))}
              </div>
            )}
            {stats.coverage.explore && (
              <div className="rounded-lg border border-teal-100 bg-teal-50/50 px-3 py-2 mb-3 text-xs">
                <p className="font-semibold text-slate-700 mb-1">
                  Company Explore (curated, ~{stats.coverage.explore.total})
                </p>
                <p className="text-slate-600">
                  {stats.coverage.explore.with_jobs}/{stats.coverage.explore.total} mit Jobs
                  ({stats.coverage.explore.pct_with_jobs}%)
                  · {stats.coverage.explore.with_domain} mit Domain
                  · {stats.coverage.explore.with_ats} ATS gepinnt
                  · {stats.coverage.explore.gaps} ohne Jobs
                </p>
                <p className="text-slate-400 mt-1">
                  Careers-Detect backfillt Domain aus Website und probiert Karriere-Pfade + ATS-APIs.
                </p>
              </div>
            )}
            {stats.coverage.seed && (
              <div className="rounded-lg border border-slate-100 bg-slate-50/80 px-3 py-2 mb-3 text-xs">
                <p className="font-semibold text-slate-700 mb-1">
                  Seed-Liste (B2B / ATS-Slugs)
                </p>
                <p className="text-slate-600">
                  {stats.coverage.seed.with_jobs}/{stats.coverage.seed.with_ats} mit ATS haben Jobs
                  ({stats.coverage.seed.pct_with_jobs}%)
                  · {stats.coverage.seed.gaps} Lücken
                  · {stats.coverage.seed.imported} in companies importiert
                  · {stats.coverage.seed.total_with_domain} mit Domain in Liste
                </p>
                {stats.coverage.seed.by_ats && stats.coverage.seed.by_ats.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {stats.coverage.seed.by_ats.map(row => (
                      <span
                        key={row.ats_type}
                        className="rounded bg-white border border-slate-200 px-2 py-0.5 text-[10px] text-slate-600"
                      >
                        {row.ats_type}: {row.with_jobs}/{row.pinned}
                      </span>
                    ))}
                  </div>
                )}
                <p className="text-slate-400 mt-1">
                  Fortune-scale: wächst über Seed + „Aus Liste“ + ats_seed_slugs — kein separates F500-File.
                </p>
              </div>
            )}
            {(stats.coverage.marquee_gaps?.length || stats.coverage.seed?.gap_sample?.length) && (
              <div className="border-t border-slate-100 pt-3">
                <p className="text-xs font-bold uppercase tracking-wide text-slate-400 mb-2">
                  Was fehlt (Sample)
                </p>
                {stats.coverage.marquee_gaps && stats.coverage.marquee_gaps.length > 0 && (
                  <div className="mb-2">
                    <p className="text-[11px] font-semibold text-slate-600 mb-1">
                      Marquee (DAX / MBB / Big Tech …)
                    </p>
                    <ul className="text-[11px] text-slate-600 space-y-0.5 max-h-24 overflow-y-auto">
                      {stats.coverage.marquee_gaps.slice(0, 12).map(g => (
                        <li key={g.domain} className="truncate">
                          <span className="font-medium">{g.name}</span>
                          <span className="text-slate-400"> · {CATEGORY_LABELS[g.category] || g.category}</span>
                          {g.reason && (
                            <span className="text-rose-600"> — {g.reason}</span>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {stats.coverage.seed?.gap_sample && stats.coverage.seed.gap_sample.length > 0 && (
                  <div>
                    <p className="text-[11px] font-semibold text-slate-600 mb-1">
                      Seed mit ATS, 0 Jobs
                    </p>
                    <ul className="text-[11px] text-slate-600 space-y-0.5 max-h-24 overflow-y-auto">
                      {stats.coverage.seed.gap_sample.slice(0, 20).map(g => (
                        <li key={g.domain} className="truncate">
                          {g.name} · {g.domain} ({g.ats_type})
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                <p className="text-[10px] text-slate-400 mt-2">
                  Rhythmus: Ingest Alle → Enrich · Lücken: Discover / Manual · Blocked DAX oft Via BA.
                </p>
              </div>
            )}
          </div>
        )}

        {/* Stat cells */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 mb-5">
          {statCells.map(s => (
            <div key={s.key} className="rounded-xl bg-white border border-slate-200 px-3 py-2.5 shadow-sm">
              <p className="text-[10px] text-slate-400 leading-tight">{s.label}</p>
              <p className="text-xl font-bold text-slate-900">{c ? c[s.key] : '-'}</p>
            </div>
          ))}
        </div>

        {/* Controls */}
        <div className="rounded-2xl bg-white border border-slate-200 p-4 shadow-sm mb-5">
          <p className="text-xs font-bold uppercase tracking-wide text-slate-400 mb-3">Aktionen</p>
          <div className="flex flex-wrap gap-2">
            <Btn label="Ingest (dry-run)" color="bg-slate-600 hover:bg-slate-700"
              onClick={() => trigger('Ingest (dry-run)', () => api.runIngest({ source: 'bundesagentur', dry_run: true }))} />
            <Btn label="Ingest BA" color="bg-pink-600 hover:bg-pink-700"
              onClick={() => trigger('Ingest BA', () => api.runIngest({
                source: 'bundesagentur', ...INGEST_FULL,
              }))} />
            <Btn label="Aus Liste" color="bg-fuchsia-600 hover:bg-fuchsia-700" icon={<Database className="w-4 h-4" />}
              onClick={() => trigger('Aus Liste', () => api.runIngest({
                source: 'list', ...INGEST_FULL,
              }))} />
            <Btn label="Big Companies" color="bg-indigo-600 hover:bg-indigo-700" icon={<Database className="w-4 h-4" />}
              onClick={() => trigger('Big Companies', () => api.runIngest({
                source: 'big', ...INGEST_FULL,
              }))} />
            <Btn label="Seed+Big (no BA)" color="bg-violet-600 hover:bg-violet-700" icon={<Database className="w-4 h-4" />}
              onClick={() => trigger('Curated ingest', () => api.runIngest({
                source: 'curated', ...INGEST_FULL,
              }))} />
            <Btn label="Detect Seeds" color="bg-teal-600 hover:bg-teal-700" icon={<Database className="w-4 h-4" />}
              onClick={() => trigger('Detect Seeds', () => api.runIngest({
                source: 'detect-seed', ...INGEST_FULL,
              }))} />
            <Btn label="Detect Explore" color="bg-teal-500 hover:bg-teal-600" icon={<Database className="w-4 h-4" />}
              onClick={() => trigger('Detect Explore', () => api.runIngest({
                source: 'detect-explore', ...INGEST_FULL,
              }))} />
            <Btn label="Ingest ATS" color="bg-pink-600 hover:bg-pink-700"
              onClick={() => trigger('Ingest ATS', () => api.runIngest({
                source: 'ats', ...INGEST_FULL,
              }))} />
            <Btn label="Ingest Alle" color="bg-pink-700 hover:bg-pink-800"
              onClick={() => trigger('Ingest Alle', () => api.runIngest({
                source: 'all', ...INGEST_FULL,
              }))} />
            <Btn label="Enrich + Index" color="bg-emerald-600 hover:bg-emerald-700" icon={<Sparkles className="w-4 h-4" />}
              onClick={() => trigger('Enrich + Index', () => api.runEmbed())} />
            <Btn label="Cleanup" color="bg-slate-600 hover:bg-slate-700" icon={<RefreshCw className="w-4 h-4" />}
              onClick={() => {
                if (window.confirm(
                  'Cleanup: Jobs ohne Re-Confirm seit 7 Tage deaktivieren, alte inaktive löschen (interessierte bleiben). Kein LLM — nur SQL.',
                )) {
                  trigger('Cleanup', () => api.runCleanup());
                }
              }} />
            <Btn label="Liveness-Check (Vorschau)" color="bg-cyan-600 hover:bg-cyan-700" icon={<RefreshCw className="w-4 h-4" />}
              onClick={() => trigger('Liveness-Check (dry)', () => api.runRevalidate({ dryRun: true }))} />
            <Btn label="Liveness-Sweep" color="bg-cyan-700 hover:bg-cyan-800" icon={<RefreshCw className="w-4 h-4" />}
              onClick={() => {
                if (window.confirm(
                  'Liveness-Sweep: Jedes Firmen-Board wird neu abgefragt. Jobs, die nicht mehr gelistet sind UND deren apply_url 404/410 liefert, werden deaktiviert. Noch-live Jobs werden aufgefrischt. Board-Fehler deaktivieren nichts (Source-Guard). Zuerst „Vorschau“ checken!',
                )) {
                  trigger('Liveness-Sweep', () => api.runRevalidate({ dryRun: false }));
                }
              }} />
            <button
              onClick={refresh}
              className="inline-flex items-center gap-1.5 rounded-lg bg-slate-100 px-3 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-200"
            >
              <RefreshCw className="w-4 h-4" /> Stats
            </button>
          </div>

          {/* Danger zone */}
          <p className="text-xs font-bold uppercase tracking-wide text-slate-400 mt-4 mb-2">Gefährlich</p>
          <div className="flex flex-wrap gap-2">
            <Btn label="Retention 14T" color="bg-amber-600 hover:bg-amber-700" icon={<Clock className="w-4 h-4" />}
              onClick={() => {
                if (window.confirm(
                  'Hard retention (last_seen): Jobs, die länger als 14 Tage nicht per Ingest bestätigt wurden, werden aus SQL + Pinecone gelöscht (interessierte bleiben).',
                )) {
                  trigger('Retention 14T', () => api.runRetention(14));
                }
              }} />
            <Btn label="RESET (DB + Pinecone)" color="bg-red-600 hover:bg-red-700" icon={<Trash2 className="w-4 h-4" />}
              onClick={() => {
                if (window.confirm('ALLES ZURÜCKSETZEN? Löscht ALLE Jobs, den gesamten Feed und alle Radare in der DB sowie beide Pinecone-Indizes. Firmen bleiben erhalten. Das ist NICHT umkehrbar.')
                    && window.confirm('Wirklich sicher? Letzte Warnung — kompletter Reset für sauberes Testen.')) {
                  trigger('RESET (DB + Pinecone)', async () => {
                    const r = await api.resetData({ dropWatchers: true, vectors: true });
                    setNote(`Reset fertig: ${runSummary(r)}`);
                    return r;
                  });
                }
              }} />
          </div>

          <p className="text-xs text-slate-500 mt-3 border-t border-slate-100 pt-3">
            Coverage-Schritte: ① Ingest Alle (14T BA, Cleanup auto) → ② Enrich+Index →
            Marquee-Lücken per Discover/Manual. Kein Extra-Migrate nötig.
          </p>
          {note && <p className="text-xs text-emerald-600 mt-3">{note}</p>}

          {stats?.last_runs && Object.keys(stats.last_runs).length > 0 && (
            <div className="mt-3 space-y-1 border-t border-slate-100 pt-3">
              {Object.entries(stats.last_runs).map(([key, r]) => (
                <div key={key} className="flex items-start gap-1.5 text-xs">
                  {r.status === 'running' ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin text-amber-500 mt-0.5 flex-shrink-0" />
                  ) : r.status === 'error' ? (
                    <XIcon className="w-3.5 h-3.5 text-red-500 mt-0.5 flex-shrink-0" />
                  ) : (
                    <CheckCheck className="w-3.5 h-3.5 text-emerald-500 mt-0.5 flex-shrink-0" />
                  )}
                  <span className="text-slate-600">
                    <span className="font-semibold capitalize">{key}</span>:{' '}
                    {r.status === 'running'
                      ? 'läuft…'
                      : r.status === 'error'
                        ? `Fehler: ${r.error}`
                        : runSummary(r.result) || 'fertig'}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Company search + manual job */}
        <div className="rounded-2xl bg-white border border-slate-200 p-4 shadow-sm mb-5">
          <p className="text-xs font-bold uppercase tracking-wide text-slate-400 mb-3">
            Firma suchen · externe ATS/Jobs · manueller Job
          </p>
          <div className="flex flex-col sm:flex-row gap-2 mb-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-2.5 w-4 h-4 text-slate-400" />
              <input
                type="text"
                value={companyQuery}
                onChange={e => {
                  setCompanyQuery(e.target.value);
                  setManualCompanyId(null);
                  setDiscoverResult(null);
                  setManualForm(f => ({ ...f, company_name: e.target.value }));
                }}
                placeholder="Name, Domain (google.com) oder Karriere-URL"
                className="w-full rounded-xl border border-slate-200 pl-9 pr-3 py-2 text-sm"
              />
              {searchLoading && (
                <Loader2 className="absolute right-3 top-2.5 w-4 h-4 animate-spin text-slate-400" />
              )}
            </div>
            <button
              type="button"
              disabled={discoverBusy || companyQuery.trim().length < 2}
              onClick={() => runDiscover(false)}
              className="inline-flex items-center justify-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:border-pink-200 hover:text-pink-700 disabled:opacity-50 shrink-0"
            >
              {discoverBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Globe className="w-4 h-4" />}
              Extern prüfen
            </button>
            <button
              type="button"
              disabled={discoverBusy || companyQuery.trim().length < 2}
              onClick={() => runDiscover(true)}
              className="inline-flex items-center justify-center gap-1.5 rounded-xl bg-pink-600 px-3 py-2 text-sm font-semibold text-white hover:bg-pink-700 disabled:opacity-50 shrink-0"
            >
              {discoverBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Database className="w-4 h-4" />}
              Jobs importieren
            </button>
          </div>
          <p className="text-[11px] text-slate-400 mb-2">
            DB-Treffer unten · „Extern prüfen“ scannt Karriereseite/ATS · „Jobs importieren“ legt Firma + Jobs an
          </p>
          <label className="flex items-center gap-2 mb-3 text-[12px] text-slate-600">
            <input
              type="checkbox"
              checked={embedAfter}
              onChange={e => setEmbedAfter(e.target.checked)}
              className="rounded border-slate-300"
            />
            Nach Import/Anlegen automatisch enrich + indexieren (LLM role_profile → Pinecone)
          </label>
          {searchResults.length > 0 && (
            <ul className="mb-3 rounded-xl border border-slate-200 bg-slate-50 max-h-40 overflow-y-auto">
              {searchResults.map(hit => (
                <li key={hit.id} className="flex items-center gap-1 border-b border-slate-100 last:border-0">
                  <button
                    type="button"
                    onClick={() => openCompanyDetail({ companyId: hit.id })}
                    className="flex-1 px-3 py-2 text-left text-sm hover:bg-pink-50 min-w-0"
                  >
                    <span className="text-[10px] font-bold text-slate-400 uppercase">In DB · </span>
                    <span className="font-semibold text-slate-800">{hit.name}</span>
                    {hit.domain && <span className="text-slate-400"> · {hit.domain}</span>}
                    <span className="text-[10px] text-emerald-700 font-semibold ml-1">
                      {hit.jobs_active} Jobs
                    </span>
                  </button>
                  <button
                    type="button"
                    onClick={() => pickCompany(hit)}
                    className="px-2 py-2 text-[10px] font-semibold text-violet-600 hover:text-violet-800 shrink-0"
                  >
                    Manual
                  </button>
                </li>
              ))}
            </ul>
          )}
          {discoverResult && (
            <div className="mb-3 rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm">
              <p className="font-semibold text-slate-800 mb-1">
                Extern: {discoverResult.display_name}
                {discoverResult.domain && <span className="text-slate-400"> · {discoverResult.domain}</span>}
              </p>
              {discoverResult.marquee_hint?.tier === 'blocked' && (
                <p className="text-[11px] text-rose-700 mb-1">
                  Kein Direct-ATS: {discoverResult.marquee_hint.reason}
                  {' '}(BA-Jobs können trotzdem im Pool sein.)
                </p>
              )}
              {discoverResult.discovery.detected ? (
                <p className="text-[11px] text-emerald-700">
                  ATS {discoverResult.discovery.ats_type}/{discoverResult.discovery.ats_slug}
                  · {discoverResult.discovery.jobs_found} Jobs gefunden
                </p>
              ) : (
                <p className="text-[11px] text-rose-700">
                  Kein ATS gefunden — {discoverResult.discovery.notes || 'manuell anlegen'}
                </p>
              )}
              {discoverResult.discovery.fetch_error && (
                <p className="text-[11px] text-amber-700">{discoverResult.discovery.fetch_error}</p>
              )}
              {discoverResult.discovery.careers_url && (
                <p className="text-[11px] text-slate-500 truncate">{discoverResult.discovery.careers_url}</p>
              )}
              {discoverResult.discovery.jobs_preview.length > 0 && (
                <ul className="mt-2 space-y-0.5 max-h-32 overflow-y-auto">
                  {discoverResult.discovery.jobs_preview.map(j => (
                    <li key={j.external_id} className="text-[11px] text-slate-600 truncate">
                      {j.title}{j.location ? ` · ${j.location}` : ''}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
          {manualCompanyId && (
            <p className="text-xs text-emerald-700 mb-2">
              ✓ Bestehende Firma in DB (ID {manualCompanyId.slice(0, 8)}…)
            </p>
          )}
          <div className="grid sm:grid-cols-2 gap-2 mb-2">
            <input
              className="rounded-lg border border-slate-200 px-3 py-2 text-sm"
              placeholder="Firma (neu oder aus Suche)"
              value={manualForm.company_name}
              onChange={e => {
                setManualCompanyId(null);
                setManualForm(f => ({ ...f, company_name: e.target.value }));
              }}
            />
            <input
              className="rounded-lg border border-slate-200 px-3 py-2 text-sm"
              placeholder="Domain (optional, z. B. google.com)"
              value={manualForm.domain}
              onChange={e => setManualForm(f => ({ ...f, domain: e.target.value }))}
            />
            <input
              className="rounded-lg border border-slate-200 px-3 py-2 text-sm sm:col-span-2"
              placeholder="Job-Titel *"
              value={manualForm.title}
              onChange={e => setManualForm(f => ({ ...f, title: e.target.value }))}
            />
            <input
              className="rounded-lg border border-slate-200 px-3 py-2 text-sm"
              placeholder="Ort"
              value={manualForm.location || ''}
              onChange={e => setManualForm(f => ({ ...f, location: e.target.value }))}
            />
            <input
              className="rounded-lg border border-slate-200 px-3 py-2 text-sm"
              placeholder="Apply-URL"
              value={manualForm.apply_url || ''}
              onChange={e => setManualForm(f => ({ ...f, apply_url: e.target.value }))}
            />
            <textarea
              className="rounded-lg border border-slate-200 px-3 py-2 text-sm sm:col-span-2 min-h-[72px]"
              placeholder="Beschreibung (optional)"
              value={manualForm.description || ''}
              onChange={e => setManualForm(f => ({ ...f, description: e.target.value }))}
            />
          </div>
          <button
            type="button"
            disabled={manualBusy}
            onClick={submitManualJob}
            className="inline-flex items-center gap-1.5 rounded-lg bg-violet-600 px-3 py-2 text-sm font-semibold text-white hover:bg-violet-700 disabled:opacity-50"
          >
            {manualBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <PlusCircle className="w-4 h-4" />}
            Manuellen Job anlegen
          </button>
          <p className="text-[11px] text-slate-400 mt-2">
            Enrichment = kanonisches role_profile (LLM oder deterministisch) · ohne Index kein Radar-Match.
            Beschreibung bei manuellen Jobs verbessert die Qualität.
          </p>
        </div>

        {/* Marquee registry */}
        {marquee && (
          <div className="rounded-2xl bg-white border border-slate-200 p-4 shadow-sm mb-5">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
              <p className="text-xs font-bold uppercase tracking-wide text-slate-400">
                Große Firmen · Status
              </p>
              <p className="text-[11px] text-slate-500">
                {marquee.summary.live} ATS · {marquee.summary.live_via_ba} via BA ·{' '}
                {marquee.summary.not_found} leer · {marquee.summary.partial} teilweise
              </p>
            </div>
            <div className="flex flex-wrap gap-1.5 mb-3">
              {Object.entries(CATEGORY_LABELS).map(([key, label]) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setMarqueeFilter(key)}
                  className={`rounded-full px-2.5 py-1 text-[11px] font-semibold border transition-colors ${
                    marqueeFilter === key
                      ? 'bg-pink-50 border-pink-200 text-pink-700'
                      : 'bg-white border-slate-200 text-slate-600 hover:border-pink-200'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-wide text-slate-400 border-b border-slate-100">
                    <th className="py-2 pr-2">Firma</th>
                    <th className="py-2 pr-2">Kategorie</th>
                    <th className="py-2 pr-2">Status</th>
                    <th className="py-2 pr-2">Jobs</th>
                    <th className="py-2 pr-2">Quellen</th>
                    <th className="py-2">Aktion</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredMarquee.map(e => {
                    const st = MARQUEE_STATUS[e.status];
                    return (
                      <tr key={e.domain} className="border-b border-slate-50">
                        <td className="py-2 pr-2">
                          <button
                            type="button"
                            onClick={() => openCompanyDetail({
                              companyId: e.company_id || undefined,
                              domain: e.domain,
                            })}
                            className="text-left hover:text-pink-700"
                          >
                            <span className="font-medium text-slate-800">{e.name}</span>
                            <span className="text-slate-400 text-xs"> · {e.domain}</span>
                          </button>
                          {e.reason && e.tier === 'blocked' && (
                            <p className="text-[10px] text-slate-400 leading-tight mt-0.5">
                              Kein Direct-ATS: {e.reason}
                            </p>
                          )}
                          {e.company_name_db && e.company_name_db !== e.name && (
                            <p className="text-[10px] text-slate-500 leading-tight mt-0.5">
                              In DB: {e.company_name_db}
                            </p>
                          )}
                          {e.ats_type && (
                            <p className="text-[10px] text-slate-400">{e.ats_type}/{e.ats_slug}</p>
                          )}
                        </td>
                        <td className="py-2 pr-2 text-xs text-slate-500">{e.category}</td>
                        <td className="py-2 pr-2">
                          <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${st.cls}`}>
                            {st.label}
                          </span>
                        </td>
                        <td className="py-2 pr-2 text-slate-600 tabular-nums">
                          <button
                            type="button"
                            className="hover:text-pink-700 font-semibold"
                            onClick={() => openCompanyDetail({
                              companyId: e.company_id || undefined,
                              domain: e.domain,
                            })}
                          >
                            {e.jobs_active}
                          </button>
                        </td>
                        <td className="py-2 pr-2 text-[11px] text-slate-500">
                          {e.jobs_ba > 0 && (
                            <span className="text-rose-700 font-medium">{e.jobs_ba} BA</span>
                          )}
                          {e.jobs_ba > 0 && e.jobs_ats > 0 && ' · '}
                          {e.jobs_ats > 0 && (
                            <span className="text-emerald-700 font-medium">{e.jobs_ats} ATS</span>
                          )}
                          {e.jobs_manual > 0 && (
                            <span className="text-violet-600">
                              {(e.jobs_ba > 0 || e.jobs_ats > 0) ? ' · ' : ''}
                              {e.jobs_manual} manual
                            </span>
                          )}
                          {e.jobs_active === 0 && '—'}
                        </td>
                        <td className="py-2">
                          <button
                            type="button"
                            onClick={() => pickMarquee(e)}
                            className="text-[11px] font-semibold text-pink-600 hover:text-pink-700"
                          >
                            Job anlegen
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Recent discoveries */}
        {stats && (stats.recent_companies.length > 0 || stats.recent_jobs.length > 0) && (
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="rounded-2xl bg-white border border-slate-200 p-4 shadow-sm">
              <p className="text-[10px] uppercase tracking-wide text-slate-400 mb-2">Neue Firmen</p>
              <ul className="space-y-1">
                {stats.recent_companies.slice(0, 10).map((co, i) => (
                  <li key={i} className="text-sm text-slate-700 truncate">
                    {co.name}{co.domain ? <span className="text-slate-400"> · {co.domain}</span> : ''}
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-2xl bg-white border border-slate-200 p-4 shadow-sm">
              <p className="text-[10px] uppercase tracking-wide text-slate-400 mb-2">
                Neue Jobs <span className="text-slate-300">· Quelle · ✨ = LLM-enriched</span>
              </p>
              <ul className="space-y-1.5">
                {stats.recent_jobs.slice(0, 10).map((j, i) => {
                  const b = sourceBadge(j.source);
                  return (
                    <li key={i} className="flex items-center gap-1.5 text-sm text-slate-700">
                      <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${b.cls} flex-shrink-0`}>{b.label}</span>
                      <span className="truncate">{j.title}<span className="text-slate-400"> · {j.company_name}</span></span>
                      <span className="ml-auto flex-shrink-0" title={j.enriched ? 'LLM-enriched (role_profile gesetzt)' : 'noch nicht enriched'}>
                        {j.enriched ? '✨' : <span className="text-slate-300">·</span>}
                      </span>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
        )}
      </div>
      {(companyDetailLoading || companyDetail || companyDetailError) && (
        <CompanyDetailModal
          data={companyDetail}
          loading={companyDetailLoading}
          error={companyDetailError}
          onClose={closeCompanyDetail}
          onPickForManual={(id, name, domain) => {
            setManualCompanyId(id);
            setManualForm(f => ({ ...f, company_name: name, domain }));
            setCompanyQuery(name);
            closeCompanyDetail();
          }}
        />
      )}
    </div>
  );
};

export default JobWatcherHub;
